// ── Products data store ───────────────────────────────────────────────────────
// Manages code-defined upsell products (accessories, add-ons, custom builds).
// Data is seeded from UPSELL_PRODUCTS on first run; edits persist to localStorage.
//
// Separate from services-store.ts — products are upsell items managed here,
// while services-store.ts is the booking form service catalogue.

import type { ServiceEntry } from '../types/services';
import { UPSELL_PRODUCTS } from '../data/upsell-products';

const STORAGE_KEY = 'gfs_admin_products_v1';

function seed(): ServiceEntry[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try { return JSON.parse(stored) as ServiceEntry[]; } catch { /* fall through */ }
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(UPSELL_PRODUCTS));
  return [...UPSELL_PRODUCTS];
}

export function getProducts(): ServiceEntry[] {
  return seed();
}

export function saveProducts(products: ServiceEntry[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
}

export function updateProduct(updated: ServiceEntry): ServiceEntry[] {
  const products = getProducts().map(p =>
    p.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : p
  );
  saveProducts(products);
  return products;
}

export function addProduct(
  entry: Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>,
): ServiceEntry[] {
  const now = new Date().toISOString();
  const newEntry: ServiceEntry = {
    ...entry,
    id: `prod-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: now,
    updatedAt: now,
  };
  const products = [...getProducts(), newEntry];
  saveProducts(products);
  return products;
}

export function deleteProduct(id: string): ServiceEntry[] {
  const products = getProducts().filter(p => p.id !== id);
  saveProducts(products);
  return products;
}

/** Reset to code-defined defaults — destructive */
export function resetProductsToInitial(): ServiceEntry[] {
  saveProducts([...UPSELL_PRODUCTS]);
  return [...UPSELL_PRODUCTS];
}

export function exportProductsJSON(): string {
  return JSON.stringify(getProducts(), null, 2);
}
